import { Component, OnInit, OnDestroy, ElementRef, inject } from '@angular/core';
import { ListenService, ListConfig, FeinbodenformEntry, BodenartEntry, BodensteckbriefEntry, ListMetadata, ListOverviewEntry, ListVersion } from './listen.service';
import { simpleListValue, simpleListExplanation, makeSimpleEntry, normalizeSimpleListImport, SimpleListItem } from './simple-list';
import { duplicatePunktartNummern } from './punktarten-editor.component';
import { buildCatalogIndex } from './feinbodenform-derivation';
import { NamensmatrixService } from './namensmatrix.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DataService } from '../data.service';
import { AuthService } from '../auth.service';
import { OfflineModeService, ConfirmService } from '@luzula/luzula-lib';
import { Subscription } from 'rxjs';

/** Sortierbare Spalten der Bodensteckbrief-Uebersicht im Editor. Spiegelt die
 *  Spalten des Katalog-Readers (BodenformenkatalogReaderComponent), damit beide
 *  Tabellen identisch aussehen und sich gleich sortieren lassen. */
type BsbSortKey = 'abkuerzung' | 'name' | 'klassifikation' | 'substratgruppe' | 'seitenzahl' | 'fbf';

@Component({
    standalone: false,
  selector: 'app-listenverwaltung',
  templateUrl: './listenverwaltung.component.html',
  styleUrls: ['./listenverwaltung.component.css'],
  
})
export class ListenverwaltungComponent implements OnInit, OnDestroy {
  private listenService = inject(ListenService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private oms = inject(OfflineModeService);
  private confirmService = inject(ConfirmService);
  private namensmatrixService = inject(NamensmatrixService);
  private host = inject(ElementRef<HTMLElement>);
  private storageSub?: Subscription;

  availableLists: ListConfig[] = [];
  selectedListName = '';
  selectedConfig?: ListConfig;

  // ===== Eingangs-Portal (Listenliste vor dem eigentlichen Editor) =====
  /** Uebersicht aller Listen mit Anzahl + "zuletzt geaendert"-Infos. Wird
   *  ueber EINEN Bulk-Request (getListsOverview) geladen, nicht pro Liste. */
  overview: ListOverviewEntry[] = [];
  /** Nach portalSearchTerm gefilterte Sicht auf overview (im Portal gerendert). */
  filteredOverview: ListOverviewEntry[] = [];
  overviewLoading = false;
  /** Filter fuer die Listenliste im Portal (sucht im Anzeigenamen). */
  portalSearchTerm = '';

  listData: any[] = [];
  filteredData: any[] = [];
  searchTerm = '';

  /** Sortierung der Bodensteckbrief-Uebersicht (reine Anzeige, wie im
   *  Katalog-Reader). Default = Seitenzahl aufsteigend (Katalog-Reihenfolge wie
   *  im gedruckten SEA Teil D). Die gespeicherte Listen-Reihenfolge (sortList)
   *  bleibt davon unberuehrt. */
  bsbSortKey: BsbSortKey = 'seitenzahl';
  bsbSortDir: 'asc' | 'desc' = 'asc';

  /** Direkt-Filter (Dropdowns) der Bodensteckbrief-Uebersicht, analog zum
   *  Katalog-Reader. '' = kein Filter aktiv. UND-verknuepft mit der Suche und
   *  untereinander. */
  bsbFilterSubstratgruppe = '';
  bsbFilterKlassifikation = '';
  /** Auswahlwerte der Dropdowns - distinct + alphabetisch aus den Eintraegen. */
  bsbSubstratgruppeOptions: string[] = [];
  bsbKlassifikationOptions: string[] = [];

  hasChanges = false;
  isCustomList = false;
  storageType: 'database' | 'local' | 'none' = 'none';
  listMetadata: ListMetadata | null = null;

  // ===== Versionshistorie =====
  /** Verlauf-Modal sichtbar? */
  showHistoryModal = false;
  /** Lädt gerade der Verlauf? */
  historyLoading = false;
  /** Frühere Stände der aktuell gewählten Liste (neueste zuerst). */
  historyVersions: ListVersion[] = [];

  /** ID des Eintrags, der nach Rueckkehr aus Editor/Vorschau zentriert und kurz
   *  hervorgehoben wird. Nur Bodensteckbriefe haben eine stabile id - andere
   *  Listentypen werden inline editiert und verlassen die Seite nie. */
  highlightedEntryId: string | null = null;

  // Bearbeitungsmodus für simple/valueNamePair
  editingIndex = -1;
  editValue = '';
  editName = '';
  // Optionale Erklaerung eines simple-Eintrags (z.B. fuer Hilfen).
  editErklaerung = '';

  // Neuer Eintrag für simple/valueNamePair
  showNewEntry = false;
  newValue = '';
  newName = '';
  newErklaerung = '';

  // Feinbodenform Modal
  showFbfModal = false;
  fbfEditMode: 'new' | 'edit' = 'new';
  fbfEditIndex = -1;
  fbfEdit: FeinbodenformEntry = this.emptyFbfEntry();
  /** true, wenn der gerade bearbeitete FBF aus dem Katalog abgeleitet ist:
   *  dann sind Name/Code/HBF/Nährkraft/Seite/Serie read-only, nur Hinweis +
   *  Zuordnungen editierbar. Manuelle Waisen (kein Katalog-Code) bleiben voll
   *  editierbar. */
  fbfEditIsDerived = false;
  /** Distinct FBF-Codes des Katalogs - Quelle fuer fbfEditIsDerived. Wird beim
   *  Oeffnen der Feinbodenformen-Liste einmalig berechnet. */
  private fbfCatalogCodes = new Set<string>();

  // Bodenart Modal
  showBodenartModal = false;
  bodenartEditMode: 'new' | 'edit' = 'new';
  bodenartEditIndex = -1;
  bodenartEdit: BodenartEntry = this.emptyBodenartEntry();

  // Zuordnungen bearbeiten
  zuordnungKeys: string[] = [];
  newZuordnungKey = '';
  newZuordnungValue = '';

  // Berechtigungspruefung. Die Listenverwaltungs-Route ist bereits durch den
  // listenverwaltungGuard abgesichert (Admin ODER Entwickler ODER Manager,
  // PERMISSIONS.md §8) - eine zusaetzliche Rollenpruefung waere doppelt.
  // Online-Pflicht bleibt: zentrale Listen sollen nicht offline lokal-only
  // veraendert werden.
  get canEdit(): boolean {
    return !this.oms.isOffline();
  }

  get disabledReason(): string {
    if (this.oms.isOffline()) {
      return 'Bearbeitung nur im Online-Modus möglich';
    }
    return '';
  }

  /** Erklaert, warum der Save-Button gerade disabled ist (oder ''). Wird im
   *  Template als Button-Tooltip + sichtbarer Hint ausgegeben. */
  get saveDisabledReason(): string {
    if (!this.canEdit) return this.disabledReason || 'Bearbeitung nicht erlaubt';
    if (!this.hasChanges) return 'Keine ungespeicherten Änderungen';
    return '';
  }

  /** Template-Helfer: reiner Wert eines simple-Eintrags (String oder Objekt). */
  simpleValue(item: SimpleListItem): string {
    return simpleListValue(item);
  }

  /** Template-Helfer: Erklaerung eines simple-Eintrags ('' wenn keine). */
  simpleExplanation(item: SimpleListItem): string {
    return simpleListExplanation(item);
  }

  ngOnInit() {
    this.availableLists = this.listenService.editableLists;
    // Gewuenschte Liste NUR aus ?list=... (Deep-Link bzw. Rueckkehr via
    // Browser-Back aus Reader/Editor) vorauswaehlen. KEIN Auto-Fallback auf die
    // erste Liste mehr: nach dem Oeffnen soll zuerst der Erklaertext + leere
    // Auswahl erscheinen, eine Liste wird erst nach aktiver Wahl angezeigt.
    const requested = this.route.snapshot.queryParamMap.get('list');
    const initial = this.availableLists.find(l => l.name === requested)?.name;
    if (initial) {
      // Deep-Link / Rueckkehr aus Editor/Vorschau (?list=...&entry=...): direkt
      // in den Editor der Liste und den zuletzt geoeffneten Eintrag zentrieren.
      const entry = this.route.snapshot.queryParamMap.get('entry');
      this.selectList(initial, entry);
    } else {
      // Kein Deep-Link: Eingangs-Portal mit der Listenliste zeigen.
      this.loadOverview();
    }

    // Auf Verbindungsänderungen reagieren und storageType aktualisieren
    this.storageSub = this.listenService.watchStorageType().subscribe(type => {
      this.storageType = type;
    });
  }

  ngOnDestroy() {
    this.storageSub?.unsubscribe();
  }

  private emptyBodenartEntry(): BodenartEntry {
    return {
      name: '',
      Lagerungsdichte: { gering: 0, mittel: 0, hoch: 0 },
      Humusgehalt: { h2: 0, h3: 0, h4: 0, h5: 0, h6: 0 }
    };
  }

  private emptyFbfEntry(): FeinbodenformEntry {
    return {
      FBF: '',
      FBF_display: '',
      name: '',
      HBF: '',
      HBF_lang: '',
      NKr: '',
      Hw: null,
      zuordnung: {},
      Serie: null,
      Seite: ''
    };
  }

  /** @param focusEntryId Optionale Eintrags-ID, auf die nach dem Laden
   *  zentriert + hervorgehoben werden soll (Rueckkehr aus Editor/Vorschau).
   *  Wird nur bei Deep-Link/Rueckkehr gesetzt, NICHT bei aktiver Dropdown-Wahl. */
  async selectList(listName: string, focusEntryId?: string | null) {
    if (this.hasChanges) {
      const confirmed = await this.confirmService.confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich wechseln?');
      if (!confirmed) {
        return;
      }
    }

    this.selectedListName = listName;
    this.selectedConfig = this.listenService.getListConfig(listName);
    this.listData = await this.listenService.getList(listName) || [];
    this.computeBsbFilterOptions();
    this.computeFbfCatalogCodes();
    this.isCustomList = await this.listenService.hasCustomList(listName);
    this.storageType = this.listenService.getStorageType();
    this.listMetadata = await this.listenService.getListMetadata(listName);
    this.hasChanges = false;
    this.cancelEdit();
    this.cancelNewEntry();
    this.closeFbfModal();
    this.closeBodenartModal();
    this.applyFilter();
    // Auswahl in der URL spiegeln (replaceUrl -> kein History-Spam beim
    // Durchklicken), damit Browser-Back aus dem Katalog-Reader wieder auf
    // dieselbe Liste zurueckkommt statt auf die Default-Liste. Das entry-Param
    // wird bei aktiver Dropdown-Wahl bewusst auf null gesetzt (= aus der URL
    // entfernt), bei Rueckkehr aber mitgefuehrt.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { list: listName, entry: focusEntryId ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (focusEntryId) {
      this.scrollToEntry(focusEntryId);
    } else {
      this.highlightedEntryId = null;
    }
  }

  /** Haelt den gerade geoeffneten Eintrag in der Listen-URL fest, BEVOR in den
   *  Editor/die Vorschau navigiert wird - so landet auch der Browser-Back-Button
   *  (nicht nur der In-App-Zurueck-Button) wieder zentriert auf dem Eintrag. */
  private rememberEntryInUrl(entryId: string): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { list: this.selectedListName, entry: entryId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Scrollt die Tabellenzeile mit der gegebenen Eintrags-ID zentriert in den
   *  Blick und hebt sie kurz hervor. Da die Tabelle nach selectList() erst im
   *  naechsten Render-Tick steht, wird ein paar Frames lang per rAF erneut
   *  versucht, bis die Zeile im DOM ist. */
  private scrollToEntry(entryId: string, attempts = 0): void {
    const root = this.host?.nativeElement as HTMLElement | undefined;
    if (!root) return;
    const safe = entryId.replace(/"/g, '\\"');
    const row = root.querySelector<HTMLElement>(`tr[data-entry-id="${safe}"]`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.highlightedEntryId = entryId;
      return;
    }
    if (attempts < 30 && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this.scrollToEntry(entryId, attempts + 1));
    }
  }

  // ========== Eingangs-Portal (Listenliste) ==========

  /** Laedt die Listen-Uebersicht fuers Portal (ein Bulk-Request). */
  async loadOverview(): Promise<void> {
    this.overviewLoading = true;
    try {
      this.overview = await this.listenService.getListsOverview();
    } catch (e) {
      console.error('Fehler beim Laden der Listen-Uebersicht:', e);
      this.overview = [];
    } finally {
      this.overviewLoading = false;
      this.applyPortalFilter();
    }
    // DB-Vollständigkeit: der Befüllen-Button bleibt sichtbar, solange noch
    // mindestens eine Liste KEIN DB-Dokument hat (isCustom=false = nur Default).
    this.dbComplete = this.overview.length > 0 && this.overview.every(o => o.isCustom);
  }

  /** Aktuelle Sortierung der Portal-Listenliste (Default: alphabetisch nach Name). */
  portalSortCol: 'displayName' | 'count' | 'updatedAt' | 'updatedBy' = 'displayName';
  portalSortDir: 'asc' | 'desc' = 'asc';

  /** Klick auf einen Portal-Spaltenkopf: Spalte setzen bzw. Richtung umschalten. */
  sortPortalBy(col: 'displayName' | 'count' | 'updatedAt' | 'updatedBy'): void {
    if (this.portalSortCol === col) {
      this.portalSortDir = this.portalSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.portalSortCol = col;
      this.portalSortDir = 'asc';
    }
    this.applyPortalFilter();
  }

  /** Filtert (nach portalSearchTerm) und sortiert die Portal-Listenliste. */
  applyPortalFilter(): void {
    const term = this.portalSearchTerm.trim().toLowerCase();
    const list = !term
      ? [...this.overview]
      : this.overview.filter(l =>
          l.displayName.toLowerCase().includes(term) ||
          l.name.toLowerCase().includes(term)
        );

    const col = this.portalSortCol;
    const dir = this.portalSortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (col === 'count') return ((a.count ?? 0) - (b.count ?? 0)) * dir;
      const av = (a[col] ?? '').toString().toLowerCase();
      const bv = (b[col] ?? '').toString().toLowerCase();
      return av.localeCompare(bv, 'de') * dir;
    });
    this.filteredOverview = list;
  }

  /** Klick auf eine Liste im Portal -> in den Editor wechseln. */
  openListFromOverview(listName: string): void {
    this.selectList(listName);
  }

  // ========== Einmal-Migration: Listen aus der Punkte-DB übernehmen ==========

  /** Läuft gerade die Befüllung? (deaktiviert den Button). */
  migrationBusy = false;
  /** True, wenn ALLE Registry-Listen ein DB-Dokument haben — blendet den Button aus. */
  dbComplete = false;

  /**
   * Admin-Aktion: überführt die historisch in `bpweb-punkte` abgelegten
   * `list_*`-Dokumente einmalig in die neue zentrale Listen-DB `bpweb-listen`.
   * Idempotent (Marker), Punkte-DB wird nur gelesen. Danach Übersicht neu laden.
   */
  async runListMigration(): Promise<void> {
    const ok = await this.confirmService.confirm({
      title: 'Listen-DB befüllen',
      message: 'Legt die zentrale Listen-Datenbank (bpweb-listen) vollständig an: ' +
        'übernimmt vorhandene Listen aus der Punkte-DB UND legt alle fehlenden ' +
        'Standardlisten an. Bereits vorhandene Listen werden nicht überschrieben. ' +
        'Fortfahren?',
      confirmText: 'Befüllen',
    });
    if (ok !== true) return;

    this.migrationBusy = true;
    try {
      const r = await this.listenService.runListMigration();
      const migrated = r.alreadyDone ? 0 : r.migrated.length;
      const msg = `Listen-DB befüllt: ${migrated} Liste(n) aus der Punkte-DB übernommen, ` +
        `${r.seeded.length} Standardliste(n) neu angelegt.`;
      await this.loadOverview();
      await this.confirmService.alert(msg, 'Listen-DB befüllt');
    } catch (e: any) {
      await this.confirmService.error(
        'Befüllen fehlgeschlagen: ' + (e?.message || String(e)),
        'Listen-DB befüllen'
      );
    } finally {
      this.migrationBusy = false;
    }
  }

  /** Aus dem Editor zurueck zum Portal. Schliesst die aktuelle Liste, raeumt
   *  list/entry aus der URL und laedt die Uebersicht neu (Anzahl/Metadaten
   *  koennen sich durch ein Speichern geaendert haben). */
  async backToOverview(): Promise<void> {
    if (this.hasChanges) {
      const ok = await this.confirmService.confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich zur Übersicht?');
      if (!ok) return;
    }
    this.selectedConfig = undefined;
    this.selectedListName = '';
    this.highlightedEntryId = null;
    this.searchTerm = '';
    this.cancelEdit();
    this.cancelNewEntry();
    this.closeFbfModal();
    this.closeBodenartModal();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { list: null, entry: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.loadOverview();
  }

  applyFilter() {
    // genericTable hat eine eigene Such-/Render-Logik in der Sub-Komponente.
    if (this.selectedConfig?.type === 'genericTable') {
      this.filteredData = this.listData;
      return;
    }
    if (!this.searchTerm) {
      this.filteredData = [...this.listData];
    } else {
      const term = this.searchTerm.toLowerCase();
      if (this.selectedConfig?.type === 'simple') {
        this.filteredData = this.listData.filter((item: SimpleListItem) =>
          simpleListValue(item).toLowerCase().includes(term) ||
          simpleListExplanation(item).toLowerCase().includes(term)
        );
      } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
        this.filteredData = this.listData.filter((item: BodenartEntry) =>
          item.name?.toLowerCase().includes(term)
        );
      } else if (this.selectedConfig?.type === 'feinbodenform') {
        this.filteredData = this.listData.filter((item: FeinbodenformEntry) =>
          item.FBF?.toLowerCase().includes(term) ||
          item.FBF_display?.toLowerCase().includes(term) ||
          item.name?.toLowerCase().includes(term) ||
          item.HBF?.toLowerCase().includes(term) ||
          item.HBF_lang?.toLowerCase().includes(term) ||
          item.NKr?.toLowerCase().includes(term) ||
          item.Seite?.toLowerCase().includes(term)
        );
      } else if (this.selectedConfig?.type === 'bodensteckbrief') {
        // termCompact = ohne Leerzeichen, fuer leerzeichen-tolerante und
        // spaltenuebergreifende Treffer (analog Bodenformenkatalog-Reader).
        const termCompact = term.replace(/\s+/g, '');
        const compact = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, '');
        this.filteredData = this.listData.filter((item: BodensteckbriefEntry) => {
          if (
            item.hauptbodenform?.abkuerzung?.toLowerCase().includes(term) ||
            item.hauptbodenform?.name?.toLowerCase().includes(term) ||
            item.hauptbodenform?.substratgruppe?.toLowerCase().includes(term) ||
            item.pdf?.toLowerCase().includes(term) ||
            String(item.page ?? '').includes(term) ||
            item.feinbodenformen?.some(f =>
              f.lokalname?.toLowerCase().includes(term) ||
              f.abkuerzung?.toLowerCase().includes(term))
          ) return true;
          // Voller FBF-Code = FBF-Abkuerzung + Klassensuffix des Steckbriefs
          // (z.B. "Me"+"SE"="MeSE", "Ft"+"S"="FtS"). Steht als zusammenhaengender
          // String in keinem einzelnen Feld -> leerzeichen-tolerant ueber tokens.
          if (termCompact) {
            const suffix = compact(item.fbf_klassensuffix);
            const tokens = [compact(item.hauptbodenform?.abkuerzung)];
            for (const f of item.feinbodenformen || []) {
              const fa = compact(f.abkuerzung);
              if (fa) tokens.push(fa + suffix);
            }
            if (tokens.some(t => t && t.includes(termCompact))) return true;
          }
          return false;
        });
      } else {
        this.filteredData = this.listData.filter(item =>
          item.value?.toLowerCase().includes(term) ||
          item.name?.toLowerCase().includes(term)
        );
      }
    }
    // Bodensteckbrief-Uebersicht wie im Katalog-Reader: Dropdown-Direktfilter
    // (UND-verknuepft mit der Suche) anwenden, dann sortierbar anzeigen. Reine
    // Anzeige-Logik - die gespeicherte Reihenfolge der Liste bleibt unveraendert.
    if (this.selectedConfig?.type === 'bodensteckbrief') {
      this.applyBsbDropdownFilters();
      this.sortBodensteckbriefView();
    }
  }

  // ========== Dropdown-Direktfilter der Bodensteckbrief-Uebersicht ==========

  /** Exakter Kategorie-Filter (Substratgruppe/Klassifikation) auf den bereits
   *  such-gefilterten Stand. Leerer Filter ('') = kein Filter. */
  private applyBsbDropdownFilters(): void {
    if (this.bsbFilterSubstratgruppe) {
      this.filteredData = this.filteredData.filter((e: BodensteckbriefEntry) =>
        (e.hauptbodenform?.substratgruppe || '') === this.bsbFilterSubstratgruppe);
    }
    if (this.bsbFilterKlassifikation) {
      this.filteredData = this.filteredData.filter((e: BodensteckbriefEntry) =>
        (e.klassifikation || '') === this.bsbFilterKlassifikation);
    }
  }

  /** Leitet die Dropdown-Auswahlwerte aus der geladenen Bodensteckbrief-Liste
   *  ab und setzt die aktiven Filter zurueck. Bei anderen Listentypen werden
   *  Optionen und Filter geleert. Wird beim Listenwechsel/Reload aufgerufen. */
  private computeBsbFilterOptions(): void {
    this.bsbFilterSubstratgruppe = '';
    this.bsbFilterKlassifikation = '';
    if (this.selectedConfig?.type !== 'bodensteckbrief') {
      this.bsbSubstratgruppeOptions = [];
      this.bsbKlassifikationOptions = [];
      return;
    }
    const list = this.listData as BodensteckbriefEntry[];
    this.bsbSubstratgruppeOptions = this.distinctSorted(list.map(e => e.hauptbodenform?.substratgruppe));
    this.bsbKlassifikationOptions = this.distinctSorted(list.map(e => e.klassifikation));
  }

  /** Distinct + alphabetisch (deutsch), leere Werte raus - fuer Dropdown-Optionen. */
  private distinctSorted(values: (string | undefined)[]): string[] {
    const set = new Set<string>();
    for (const v of values) {
      const s = (v || '').trim();
      if (s) set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'de'));
  }

  // ========== Sortierung der Bodensteckbrief-Uebersicht ==========

  /** Spaltenkopf-Klick: gleiche Spalte -> Richtung umdrehen, sonst neue Spalte
   *  aufsteigend. Danach neu filtern+sortieren. */
  setBsbSort(key: BsbSortKey): void {
    if (this.bsbSortKey === key) {
      this.bsbSortDir = this.bsbSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.bsbSortKey = key;
      this.bsbSortDir = 'asc';
    }
    this.applyFilter();
  }

  /** Bootstrap-Icon-Klasse fuer den Sortierindikator einer Spalte (identisch zum
   *  Katalog-Reader). */
  bsbSortIcon(key: BsbSortKey): string {
    if (this.bsbSortKey !== key) return 'bi-arrow-down-up';
    return this.bsbSortDir === 'asc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill';
  }

  /** Sortiert filteredData nach aktiver Spalte/Richtung. Richtung wirkt nur auf
   *  die Primaerspalte; bei Gleichstand stabil nach HBF-Abkuerzung + Name. */
  private sortBodensteckbriefView(): void {
    const dir = this.bsbSortDir === 'desc' ? -1 : 1;
    this.filteredData = [...this.filteredData].sort((a: BodensteckbriefEntry, b: BodensteckbriefEntry) => {
      let cmp = this.bsbPrimaryCompare(a, b) * dir;
      if (cmp === 0) {
        cmp = (a.hauptbodenform?.abkuerzung || '').localeCompare(b.hauptbodenform?.abkuerzung || '', 'de') ||
          (a.hauptbodenform?.name || '').localeCompare(b.hauptbodenform?.name || '', 'de');
      }
      return cmp;
    });
  }

  private bsbPrimaryCompare(a: BodensteckbriefEntry, b: BodensteckbriefEntry): number {
    switch (this.bsbSortKey) {
      case 'seitenzahl':
        return this.bsbNumCompare(parseInt(a.seitenzahl ?? '', 10), parseInt(b.seitenzahl ?? '', 10));
      case 'fbf':
        return (a.feinbodenformen?.length || 0) - (b.feinbodenformen?.length || 0);
      case 'name':
        return (a.hauptbodenform?.name || '').localeCompare(b.hauptbodenform?.name || '', 'de');
      case 'klassifikation':
        return (a.klassifikation || '').localeCompare(b.klassifikation || '', 'de');
      case 'substratgruppe':
        return (a.hauptbodenform?.substratgruppe || '').localeCompare(b.hauptbodenform?.substratgruppe || '', 'de');
      case 'abkuerzung':
      default:
        return (a.hauptbodenform?.abkuerzung || '').localeCompare(b.hauptbodenform?.abkuerzung || '', 'de');
    }
  }

  /** Numerischer Vergleich; leere/ungueltige Werte landen hinter gueltigen. */
  private bsbNumCompare(an: number, bn: number): number {
    const av = !Number.isNaN(an);
    const bv = !Number.isNaN(bn);
    if (av && bv) return an - bn;
    if (av) return -1;
    if (bv) return 1;
    return 0;
  }

  // Bearbeitung für simple/valueNamePair
  startEdit(index: number) {
    if (this.selectedConfig?.type === 'feinbodenform') {
      this.startFbfEdit(index);
      return;
    }
    if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.startBodenartEdit(index);
      return;
    }
    if (this.selectedConfig?.type === 'bodensteckbrief') {
      this.openSteckbrief(index);
      return;
    }
    const realIndex = this.getRealIndex(index);
    this.editingIndex = realIndex;
    const item = this.listData[realIndex];
    if (this.selectedConfig?.type === 'simple') {
      this.editValue = simpleListValue(item);
      this.editErklaerung = simpleListExplanation(item);
    } else {
      this.editValue = item.value;
      this.editName = item.name;
    }
  }

  saveEdit() {
    if (this.editingIndex < 0) return;

    if (this.selectedConfig?.type === 'simple') {
      if (!this.editValue.trim()) {
        void this.confirmService.alert('Wert darf nicht leer sein', 'Hinweis');
        return;
      }
      this.listData[this.editingIndex] = makeSimpleEntry(this.editValue, this.editErklaerung);
    } else {
      if (!this.editValue.trim()) {
        void this.confirmService.alert('Wert darf nicht leer sein', 'Hinweis');
        return;
      }
      this.listData[this.editingIndex] = {
        value: this.editValue.trim(),
        name: this.editName.trim()
      };
    }

    this.hasChanges = true;
    this.cancelEdit();
    this.applyFilter();
  }

  cancelEdit() {
    this.editingIndex = -1;
    this.editValue = '';
    this.editName = '';
    this.editErklaerung = '';
  }

  // Neuer Eintrag für simple/valueNamePair
  startNewEntry() {
    if (this.selectedConfig?.type === 'feinbodenform') {
      this.startFbfNew();
      return;
    }
    if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.startBodenartNew();
      return;
    }
    if (this.selectedConfig?.type === 'bodensteckbrief') {
      // Interaktive Neuanlage im Voll-Editor: leerer Steckbrief, die ID wird
      // erst beim ersten Speichern vergeben (manuell#<n>). 'neu' ist der
      // Sentinel der Editor-Route (siehe BodensteckbriefEditorComponent).
      this.router.navigate(['/listenverwaltung/steckbrief', 'neu', 'edit']);
      return;
    }
    this.showNewEntry = true;
    this.newValue = '';
    this.newName = '';
    this.newErklaerung = '';
  }

  addNewEntry() {
    if (this.selectedConfig?.type === 'simple') {
      if (!this.newValue.trim()) {
        void this.confirmService.alert('Wert darf nicht leer sein', 'Hinweis');
        return;
      }
      this.listData.push(makeSimpleEntry(this.newValue, this.newErklaerung));
    } else {
      if (!this.newValue.trim()) {
        void this.confirmService.alert('Wert darf nicht leer sein', 'Hinweis');
        return;
      }
      this.listData.push({
        value: this.newValue.trim(),
        name: this.newName.trim()
      });
    }

    this.hasChanges = true;
    this.cancelNewEntry();
    this.sortList();
    this.applyFilter();
  }

  cancelNewEntry() {
    this.showNewEntry = false;
    this.newValue = '';
    this.newName = '';
    this.newErklaerung = '';
  }

  /** Steht der JSON-Import zur Verfuegung? Nur fuer `simple`-Listen (Forstaemter,
   *  Wuchsgebiete, …) — `genericTable` hat einen eigenen Import in der
   *  Sub-Komponente, strukturierte Typen (Feinbodenform/Bodenart/Steckbrief)
   *  werden ueber ihre Modale gepflegt. */
  get canImportList(): boolean {
    return this.selectedConfig?.type === 'simple';
  }

  /** Datei-Auswahl des „Import (JSON)"-Buttons fuer simple-Listen. */
  onImportListFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      void this.applyListImport(String(reader.result));
      input.value = '';
    };
    reader.readAsText(file);
  }

  /**
   * Ersetzt die aktuelle simple-Liste durch ein hochgeladenes JSON-Array
   * (mit Rueckfrage, alt→neu-Anzahl). REPLACE statt Merge: die Datei ist das
   * Soll, ein Merge gegen einen anders formatierten DB-Stand wuerde nur
   * Dubletten erzeugen. Speichert NICHT — der Nutzer prueft und loest das
   * Speichern in die zentrale DB anschliessend selbst aus.
   */
  private async applyListImport(text: string): Promise<void> {
    if (!this.canImportList) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      await this.confirmService.error('Die Datei enthält kein gültiges JSON.', 'Import fehlgeschlagen');
      return;
    }
    if (!Array.isArray(parsed)) {
      await this.confirmService.error('Die Datei muss ein JSON-Array sein.', 'Import fehlgeschlagen');
      return;
    }
    const incoming = normalizeSimpleListImport(parsed);
    if (incoming.length === 0) {
      await this.confirmService.error('Die Datei enthält keine gültigen Einträge.', 'Import fehlgeschlagen');
      return;
    }
    const listLabel = this.selectedConfig?.displayName || this.selectedListName;
    const ok = await this.confirmService.confirm({
      title: 'Liste ersetzen',
      message: `Die Liste „${listLabel}" (${this.listData.length} Einträge) vollständig durch die ` +
        `${incoming.length} Einträge aus der Datei ersetzen? Bestehende Einträge werden entfernt. ` +
        'Gespeichert wird erst nach deiner Prüfung über „Speichern".',
      confirmText: 'Ersetzen',
    });
    if (ok !== true) return;
    this.listData = incoming;
    this.hasChanges = true;
    this.sortList();
    this.applyFilter();
    await this.confirmService.alert(
      `${incoming.length} Einträge importiert. Bitte prüfen und anschließend „Speichern", ` +
      'um die zentrale Liste zu aktualisieren.',
      'Import übernommen'
    );
  }

  // Löschen
  async deleteEntry(index: number) {
    const realIndex = this.getRealIndex(index);
    const item = this.listData[realIndex];
    let displayValue: string;
    if (this.selectedConfig?.type === 'simple') {
      displayValue = simpleListValue(item);
    } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
      displayValue = item.name;
    } else if (this.selectedConfig?.type === 'feinbodenform') {
      displayValue = item.FBF_display || item.FBF;
    } else if (this.selectedConfig?.type === 'bodensteckbrief') {
      displayValue = item.hauptbodenform?.abkuerzung || item.hauptbodenform?.name || item.id;
    } else {
      displayValue = item.value;
    }

    const confirmed = await this.confirmService.confirmDelete(`"${displayValue}" wirklich löschen?`);
    if (confirmed) {
      this.listData.splice(realIndex, 1);
      this.hasChanges = true;
      this.applyFilter();
    }
  }

  // Sortieren
  sortList() {
    // genericTable: Reihenfolge bleibt wie in den Daten (kein generisches Sort).
    if (this.selectedConfig?.type === 'genericTable') return;
    if (this.selectedConfig?.type === 'simple') {
      this.listData.sort((a: SimpleListItem, b: SimpleListItem) =>
        simpleListValue(a).localeCompare(simpleListValue(b), 'de')
      );
    } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.listData.sort((a: BodenartEntry, b: BodenartEntry) =>
        (a.name || '').localeCompare(b.name || '', 'de')
      );
    } else if (this.selectedConfig?.type === 'feinbodenform') {
      this.listData.sort((a: FeinbodenformEntry, b: FeinbodenformEntry) =>
        (a.FBF || '').localeCompare(b.FBF || '', 'de')
      );
    } else if (this.selectedConfig?.type === 'bodensteckbrief') {
      this.listData.sort((a: BodensteckbriefEntry, b: BodensteckbriefEntry) => {
        const aa = a.hauptbodenform?.abkuerzung || '';
        const bb = b.hauptbodenform?.abkuerzung || '';
        return aa.localeCompare(bb, 'de') ||
          (a.hauptbodenform?.name || '').localeCompare(b.hauptbodenform?.name || '', 'de');
      });
    } else {
      this.listData.sort((a, b) => a.value.localeCompare(b.value, 'de'));
    }
  }

  // Speichern
  async saveList() {
    // Diagnose-Log: oft wird "Speichern geht nicht" durch disabled-State
    // oder stillen LocalStorage-Quota-Fehler bei grossen Listen ausgeloest.
    // Mit dem Log kann der Auslöser im DevTools-Console sofort identifiziert
    // werden, ohne das UI zu spammen.
    console.log('[Listenverwaltung] saveList',
      { list: this.selectedListName, n: this.listData.length,
        hasChanges: this.hasChanges, canEdit: this.canEdit,
        storageType: this.storageType });
    // Eindeutigkeits-Schutz: doppelte Punktart-Nummern wuerden den bp.type-Lookup
    // mehrdeutig machen — Speichern blockieren, bis korrigiert. (Reines Zufuegen
    // und Umbenennen bleibt erlaubt; die Nummer-Sperre bestehender Eintraege sitzt
    // im Punktarten-Editor.)
    if (this.selectedListName === 'Punktarten') {
      const dups = duplicatePunktartNummern(this.listData);
      if (dups.length) {
        await this.confirmService.error(
          'Doppelte Punktart-Nummern: ' + dups.join(', ') +
          '. Nummern müssen eindeutig sein – bitte korrigieren und erneut speichern.',
          'Speichern nicht möglich');
        return;
      }
    }
    try {
      this.sortList();
      await this.listenService.saveList(this.selectedListName, this.listData);
      await this.dataService.reloadCustomList(this.selectedListName);
      this.listMetadata = await this.listenService.getListMetadata(this.selectedListName);
      this.hasChanges = false;
      this.isCustomList = true;
      await this.confirmService.alert('Liste gespeichert.', 'Gespeichert');
    } catch (e: any) {
      console.error('Fehler beim Speichern:', e);
      const quota = (e?.name === 'QuotaExceededError') ||
                    /quota|exceeded|too\s*large/i.test(e?.message || '');
      const hint = quota
        ? '\n\nHinweis: Liste vermutlich zu gross für LocalStorage (~5 MB).\n'
          + 'Bei vielen Steckbriefen ist eine DB-Verbindung nötig (storageType="database").'
        : '';
      await this.confirmService.error('Fehler beim Speichern: ' + (e?.message || e) + hint, 'Speichern fehlgeschlagen');
    }
  }

  // Verwerfen - lädt gespeicherten Stand neu
  async discardChanges() {
    const confirmed = await this.confirmService.confirmDiscard('Alle ungespeicherten Änderungen verwerfen?');
    if (!confirmed) {
      return;
    }

    this.listData = await this.listenService.getList(this.selectedListName) || [];
    this.computeBsbFilterOptions();
    this.computeFbfCatalogCodes();
    this.hasChanges = false;
    this.cancelEdit();
    this.cancelNewEntry();
    this.closeFbfModal();
    this.closeBodenartModal();
    this.applyFilter();
  }

  // ========== Versionshistorie ==========

  /** Öffnet den Verlauf der aktuell gewählten Liste und lädt die Versionen
   *  (Online-Abfrage gegen die zentrale DB; offline bleibt die Liste leer). */
  async openHistory(): Promise<void> {
    if (!this.selectedListName) return;
    this.showHistoryModal = true;
    this.historyLoading = true;
    this.historyVersions = [];
    try {
      this.historyVersions = await this.listenService.getListHistory(this.selectedListName);
    } catch (e) {
      console.error('Fehler beim Laden des Verlaufs:', e);
      this.historyVersions = [];
    } finally {
      this.historyLoading = false;
    }
  }

  closeHistory(): void {
    this.showHistoryModal = false;
    this.historyVersions = [];
  }

  /** Lesbare Beschriftung der Aktion, die einen Schnappschuss erzeugt hat. */
  versionActionLabel(action: ListVersion['action']): string {
    switch (action) {
      case 'reset': return 'vor Zurücksetzen';
      case 'restore': return 'vor Wiederherstellen';
      default: return 'vor Änderung';
    }
  }

  /**
   * Stellt eine frühere Version der Liste wieder her (mit Rückfrage). Der aktuell
   * gespeicherte Stand wandert dabei selbst in den Verlauf, geht also nicht
   * verloren — Wiederherstellen ist umkehrbar. Lädt anschließend die Liste neu
   * in den Editor.
   */
  async restoreVersion(version: ListVersion): Promise<void> {
    if (!this.canEdit) {
      await this.confirmService.error(this.disabledReason || 'Bearbeitung nicht erlaubt', 'Wiederherstellen');
      return;
    }
    const when = this.formatDate(version.updatedAt) || this.formatDate(version.savedAt) || 'diesen Stand';
    const ok = await this.confirmService.confirm({
      title: 'Version wiederherstellen',
      message: `Den Stand vom ${when} (${version.count} Einträge) wiederherstellen? Der aktuelle Stand wird ` +
        'dabei ebenfalls im Verlauf gesichert und kann jederzeit wieder zurückgeholt werden.',
      confirmText: 'Wiederherstellen',
    });
    if (ok !== true) return;
    try {
      await this.listenService.restoreVersion(this.selectedListName, version.id);
      await this.dataService.reloadCustomList(this.selectedListName);
      this.listData = await this.listenService.getList(this.selectedListName) || [];
      this.computeBsbFilterOptions();
      this.computeFbfCatalogCodes();
      this.listMetadata = await this.listenService.getListMetadata(this.selectedListName);
      this.isCustomList = true;
      this.hasChanges = false;
      this.cancelEdit();
      this.cancelNewEntry();
      this.applyFilter();
      this.closeHistory();
      await this.confirmService.alert('Version wiederhergestellt.', 'Wiederhergestellt');
    } catch (e: any) {
      console.error('Fehler beim Wiederherstellen:', e);
      await this.confirmService.error('Wiederherstellen fehlgeschlagen: ' + (e?.message || e), 'Wiederherstellen fehlgeschlagen');
    }
  }

  // Formatiert ISO-Datum für Anzeige
  formatDate(isoDate: string | null | undefined): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Zurück zur Startseite
  async goBack() {
    if (this.hasChanges) {
      const confirmed = await this.confirmService.confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich verlassen?');
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/']);
  }

  // Hilfsfunktionen
  private getRealIndex(filteredIndex: number): number {
    const filteredItem = this.filteredData[filteredIndex];
    return this.listData.indexOf(filteredItem);
  }

  isEditing(index: number): boolean {
    return this.getRealIndex(index) === this.editingIndex;
  }

  // ========== Feinbodenform Modal ==========

  /** Distinct FBF-Codes des Katalogs ableiten (nur fuer die Feinbodenformen-
   *  Liste relevant) - Quelle fuer den Read-only-Modus des FBF-Editors. */
  private computeFbfCatalogCodes(): void {
    if (this.selectedConfig?.type !== 'feinbodenform') {
      this.fbfCatalogCodes = new Set<string>();
      return;
    }
    const catalog = (this.listenService.getSync('Bodensteckbriefe') as BodensteckbriefEntry[]) || [];
    this.fbfCatalogCodes = new Set(buildCatalogIndex(catalog).byCode.keys());
  }

  /** true, wenn der FBF-Code aus dem Katalog abgeleitet ist (dann Kernfelder
   *  read-only). Leerzeichen-toleranter Vergleich (Code wird ohne Leerzeichen
   *  geschluesselt). */
  private isDerivedFbf(code: string | undefined): boolean {
    return this.fbfCatalogCodes.has((code || '').replace(/\s+/g, ''));
  }

  startFbfNew() {
    this.fbfEditMode = 'new';
    this.fbfEditIndex = -1;
    this.fbfEdit = this.emptyFbfEntry();
    // Neuanlage = manueller Eintrag (kein Katalog-Bezug) -> alle Felder editierbar.
    this.fbfEditIsDerived = false;
    this.zuordnungKeys = [];
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
    this.showFbfModal = true;
  }

  startFbfEdit(filteredIndex: number) {
    const realIndex = this.getRealIndex(filteredIndex);
    this.fbfEditMode = 'edit';
    this.fbfEditIndex = realIndex;
    const item = this.listData[realIndex] as FeinbodenformEntry;
    // Deep copy
    this.fbfEdit = {
      FBF: item.FBF || '',
      FBF_display: item.FBF_display || '',
      name: item.name || '',
      HBF: item.HBF || '',
      HBF_lang: item.HBF_lang || '',
      NKr: item.NKr || '',
      Hw: item.Hw,
      zuordnung: { ...item.zuordnung },
      Serie: item.Serie,
      Seite: item.Seite || ''
    };
    // Katalog-abgeleitete FBFs: Kernfelder read-only (nur Hinweis/Zuordnungen
    // editierbar). Manuelle Waisen bleiben voll editierbar.
    this.fbfEditIsDerived = this.isDerivedFbf(item.FBF);
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
    this.showFbfModal = true;
  }

  closeFbfModal() {
    this.showFbfModal = false;
    this.fbfEdit = this.emptyFbfEntry();
    this.zuordnungKeys = [];
  }

  saveFbfEntry() {
    if (!this.fbfEdit.FBF.trim()) {
      void this.confirmService.alert('FBF darf nicht leer sein', 'Hinweis');
      return;
    }

    // Leere Strings in null umwandeln
    const entry: FeinbodenformEntry = {
      ...this.fbfEdit,
      Hw: this.fbfEdit.Hw?.trim() || null,
      Serie: this.fbfEdit.Serie?.trim() || null
    };

    if (this.fbfEditMode === 'new') {
      this.listData.push(entry);
    } else {
      this.listData[this.fbfEditIndex] = entry;
    }

    this.hasChanges = true;
    this.closeFbfModal();
    this.sortList();
    this.applyFilter();
  }

  // Zuordnungen verwalten
  addZuordnung() {
    if (!this.newZuordnungKey.trim()) {
      void this.confirmService.alert('Schlüssel darf nicht leer sein', 'Hinweis');
      return;
    }
    this.fbfEdit.zuordnung[this.newZuordnungKey.trim()] = this.newZuordnungValue.trim();
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
  }

  removeZuordnung(key: string) {
    delete this.fbfEdit.zuordnung[key];
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
  }

  updateZuordnung(key: string, value: string) {
    this.fbfEdit.zuordnung[key] = value;
  }

  // ========== Bodenart Modal ==========

  startBodenartNew() {
    this.bodenartEditMode = 'new';
    this.bodenartEditIndex = -1;
    this.bodenartEdit = this.emptyBodenartEntry();
    this.showBodenartModal = true;
  }

  startBodenartEdit(filteredIndex: number) {
    const realIndex = this.getRealIndex(filteredIndex);
    this.bodenartEditMode = 'edit';
    this.bodenartEditIndex = realIndex;
    const item = this.listData[realIndex] as BodenartEntry;
    this.bodenartEdit = {
      name: item.name || '',
      Lagerungsdichte: { ...item.Lagerungsdichte },
      Humusgehalt: { ...item.Humusgehalt }
    };
    this.showBodenartModal = true;
  }

  closeBodenartModal() {
    this.showBodenartModal = false;
    this.bodenartEdit = this.emptyBodenartEntry();
  }

  saveBodenartEntry() {
    if (!this.bodenartEdit.name.trim()) {
      void this.confirmService.alert('Kürzel darf nicht leer sein', 'Hinweis');
      return;
    }

    const entry: BodenartEntry = {
      name: this.bodenartEdit.name.trim(),
      Lagerungsdichte: { ...this.bodenartEdit.Lagerungsdichte },
      Humusgehalt: { ...this.bodenartEdit.Humusgehalt }
    };

    if (this.bodenartEditMode === 'new') {
      this.listData.push(entry);
    } else {
      this.listData[this.bodenartEditIndex] = entry;
    }

    this.hasChanges = true;
    this.closeBodenartModal();
    this.sortList();
    this.applyFilter();
  }

  // ========== Bodensteckbrief Browser / Editor-Stub ==========

  async openSteckbrief(filteredIndex: number) {
    if (this.hasChanges) {
      const ok = await this.confirmService.confirmDiscard(
        'Es gibt ungespeicherte Änderungen in der Liste. Trotzdem zum Editor wechseln?'
      );
      if (!ok) return;
    }
    const realIndex = this.getRealIndex(filteredIndex);
    const item = this.listData[realIndex] as BodensteckbriefEntry;
    await this.rememberEntryInUrl(item.id);
    this.router.navigate(['/listenverwaltung/steckbrief', item.id, 'edit']);
  }

  async previewSteckbrief(filteredIndex: number) {
    if (this.hasChanges) {
      const ok = await this.confirmService.confirmDiscard(
        'Es gibt ungespeicherte Änderungen. Trotzdem zur Vorschau wechseln?'
      );
      if (!ok) return;
    }
    const realIndex = this.getRealIndex(filteredIndex);
    const item = this.listData[realIndex] as BodensteckbriefEntry;
    await this.rememberEntryInUrl(item.id);
    // Router macht das URL-Encoding fuer "#" in der ID selbststaendig, wenn
    // wir das commands-Array benutzen (nicht navigateByUrl).
    this.router.navigate(['/listenverwaltung/steckbrief', item.id]);
  }

  /** Namensmatrix (Kürzel × Substrat) als Modal öffnen. Der Service merkt sich
   *  diese Listen-URL als Ursprung: Schließen ohne Zellklick (bzw. „Zurück" aus
   *  dem Viewer nach einem Zellklick) führt wieder hierher zurück. */
  openNamensmatrix(): void {
    this.namensmatrixService.open();
  }

  // ========== (i)-Icon: Sprung zum Bodenformenkatalog-Reader ==========

  /** Liefert die Bodensteckbrief-ID, deren hauptbodenform.abkuerzung oder
   *  -name auf das uebergebene Kuerzel matched. Wir nutzen die im
   *  ListenService bereits gecachte Bodensteckbriefe-Liste (sync via
   *  memoryCache), damit wir nicht jedes Mal nachladen muessen. Wenn die
   *  Liste noch nicht geladen ist, liefert die Methode null - in dem Fall
   *  faellt der Reader-Aufrufer auf eine Search-Query zurueck. */
  private lookupBodensteckbriefId(value: string): string | null {
    if (!value) return null;
    const term = value.trim().toLowerCase();
    const list = (this.listenService as any).memoryCache?.get('Bodensteckbriefe') as BodensteckbriefEntry[] | undefined;
    if (!Array.isArray(list)) return null;
    const hit = list.find(e =>
      (e.hauptbodenform?.abkuerzung || '').toLowerCase() === term ||
      (e.hauptbodenform?.name || '').toLowerCase() === term
    );
    return hit ? hit.id : null;
  }

  /** Click auf das (i)-Icon eines HBF-/FBF-Felds: Springt in den
   *  Bodenformen-Katalog-Reader. Wenn wir die exakte Bodensteckbrief-ID
   *  zuordnen koennen, navigieren wir direkt auf /bodenformenkatalog/:id;
   *  sonst geben wir den Wert als Search-Term mit, und der Reader landet
   *  auf dem ersten gefilterten Treffer. In beiden Faellen nutzt der Reader
   *  intern replaceUrl fuer Prev/Next, der Browser-Back springt also direkt
   *  in die Listenverwaltung zurueck. */
  openBodenformenkatalog(value: string): void {
    if (!value || !value.trim()) return;
    const id = this.lookupBodensteckbriefId(value);
    if (id) {
      this.router.navigate(['/bodenformenkatalog', id]);
    } else {
      this.router.navigate(['/bodenformenkatalog'], { queryParams: { search: value.trim() } });
    }
  }

}
